import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { ordersAPI, b2bAPI, authAPI } from "../services/api";
import { getPlaceSuggestions, resolveDelivery } from "../services/mapsService";
import { openPaystackPopup, pollPaymentStatus } from "../services/paystackService";
import CodeWindow from "../components/CodeWindow";
import { CheckCircle, Zap, MapPin, Shield, Loader } from "lucide-react";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

// ── Scroll reveal hook ────────────────────────────────────────────────────────
function useReveal() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add("on")),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ── Service options ───────────────────────────────────────────────────────────
const SERVICES = [
  { id:"parcel",   icon:"📦", label:"Parcel" },
  { id:"food",     icon:"🍔", label:"Food" },
  { id:"pharmacy", icon:"💊", label:"Pharmacy" },
  { id:"grocery",  icon:"🛒", label:"Grocery" },
];

const ORDER_STATUSES = [
  { key:"pending",        label:"Order placed" },
  { key:"accepted",       label:"Order accepted" },
  { key:"rider_assigned", label:"Rider assigned" },
  { key:"picked_up",      label:"Package picked up" },
  { key:"on_the_way",     label:"On the way" },
  { key:"delivered",      label:"Delivered ✓" },
];

// ── API Key Form ──────────────────────────────────────────────────────────────
function ApiKeyForm() {
  const navigate = useNavigate();
  const [name,     setName]     = useState("");
  const [bizName,  setBizName]  = useState("");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [pwVisible,setPwVisible]= useState(false);
  const [copied,   setCopied]   = useState("");

  const copyText = (text, label) => {
    navigator.clipboard?.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(""), 2000);
  };

  const submit = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    if (!name)                 { toast.error("Enter your name");    return; }
    if (password.length < 6)   { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const r = await b2bAPI.register({
        name,
        business_name: bizName || name,
        email,
        password,
      });
      setResult({ ...r.data.data, isNew: true, enteredPassword: password });
      localStorage.setItem("kq_b2b_key",   r.data.data.api_key);
      localStorage.setItem("kq_b2b_secret", r.data.data.api_secret);
      localStorage.setItem("kq_b2b_email",  email);
      toast.success("✅ Account created! Your API key is ready.");
    } catch(e) {
      if (e.response?.data?.message?.includes("already registered")) {
        toast("Account already exists. Sign in to your developer portal.", { icon:"👋" });
        navigate("/developer");
      } else {
        toast.error(e.response?.data?.message || "Could not create account");
      }
    }
    setLoading(false);
  };

  if (result) {
    return (
      <div style={{ background:"var(--bg3)", border:"1px solid rgba(245,166,35,0.3)", borderRadius:14, padding:24, animation:"fadeUp 0.4s ease both" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
          <span style={{ fontSize:24 }}>🎉</span>
          <div>
            <div style={{ fontFamily:"var(--font-head)", fontSize:16, fontWeight:700 }}>Account Created!</div>
            <div style={{ fontSize:12, color:"var(--text2)" }}>Welcome {result.name} — your credentials are below</div>
          </div>
        </div>
        {[
          { label:"API KEY",        value: result.api_key,        hint:"Use as X-API-Key header" },
          { label:"API SECRET",     value: result.api_secret,     hint:"For HMAC signature verification" },
          { label:"YOUR PASSWORD",  value: result.enteredPassword, hint:"Save this — use it to log into your portal" },
        ].map(row => (
          <div key={row.label} style={{ background:"var(--bg4)", borderRadius:9, padding:"11px 14px", marginBottom:10 }}>
            <div style={{ fontSize:10, color:"var(--text3)", fontFamily:"var(--font-mono)", letterSpacing:"0.8px", marginBottom:4 }}>{row.label}</div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:12, color:"var(--accent)", flex:1, wordBreak:"break-all" }}>
                {row.label === "YOUR PASSWORD" && !pwVisible ? "•".repeat(row.value.length) : row.value}
              </div>
              {row.label === "YOUR PASSWORD" && (
                <button className="btn-ghost btn-sm" onClick={() => setPwVisible(v=>!v)} style={{ flexShrink:0 }}>
                  {pwVisible ? "Hide" : "Show"}
                </button>
              )}
              <button className="btn-primary btn-sm" onClick={() => copyText(row.value, row.label)} style={{ flexShrink:0 }}>
                {copied === row.label ? "✓" : "Copy"}
              </button>
            </div>
            <div style={{ fontSize:11, color:"var(--text3)", marginTop:4 }}>{row.hint}</div>
          </div>
        ))}
        <button className="btn-primary" style={{ width:"100%", justifyContent:"center", marginTop:4 }}
          onClick={() => navigate("/developer")}>
          Go to Developer Portal →
        </button>
      </div>
    );
  }

  return (
    <div style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:14, padding:24 }}>
      <div style={{ fontFamily:"var(--font-head)", fontSize:16, fontWeight:700, marginBottom:5 }}>Get your free API key</div>
      <p style={{ fontSize:13, color:"var(--text2)", marginBottom:18 }}>
        Register your business and start sending deliveries today. No credit card required.
      </p>
      <div className="form-row" style={{ marginBottom:10 }}>
        <input className="form-input" placeholder="Your name *" value={name}    onChange={e=>setName(e.target.value)}/>
        <input className="form-input" placeholder="Business name" value={bizName} onChange={e=>setBizName(e.target.value)}/>
      </div>
      <input className="form-input" style={{ marginBottom:10 }} type="email" placeholder="your@business.com *"
        value={email} onChange={e=>setEmail(e.target.value)}/>
      <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
        <input className="form-input" style={{ flex:1, minWidth:160 }} type="password" placeholder="Choose a password *"
          value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()}/>
        <button className="btn-primary btn-sm" onClick={submit} disabled={loading} style={{ flexShrink:0 }}>
          {loading ? <span className="spinner"/> : "Get Key →"}
        </button>
      </div>
      <p style={{ fontSize:11, color:"var(--text3)", marginTop:10 }}>
        Already have an account? <span style={{ color:"var(--accent)", cursor:"pointer" }} onClick={() => navigate("/developer")}>Sign in to your portal →</span>
      </p>
    </div>
  );
}

// ── Address input with autocomplete ──────────────────────────────────────────
function AddressInput({ label, placeholder, value, onChange, onSelect, error }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open,        setOpen]        = useState(false);
  const [fetching,    setFetching]    = useState(false);
  const debounce = useRef();

  const handleChange = (v) => {
    onChange(v);
    clearTimeout(debounce.current);
    if (v.length < 3) { setSuggestions([]); setOpen(false); return; }
    setFetching(true);
    debounce.current = setTimeout(async () => {
      const results = await getPlaceSuggestions(v);
      setSuggestions(results);
      setOpen(results.length > 0);
      setFetching(false);
    }, 380);
  };

  const pick = (s) => {
    onChange(s.description);
    onSelect(s.description);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div className="form-group" style={{ position:"relative" }}>
      <label className="form-label">{label}</label>
      <div style={{ position:"relative" }}>
        <input
          className={`form-input ${error?"err":""}`}
          placeholder={placeholder}
          value={value}
          onChange={e => handleChange(e.target.value)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          onFocus={() => suggestions.length && setOpen(true)}
          autoComplete="off"
        />
        {fetching && (
          <div style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)" }}>
            <Loader size={14} style={{ color:"var(--text3)", animation:"spin 0.8s linear infinite" }}/>
          </div>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div style={{
          position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:100,
          background:"var(--bg3)", border:"1px solid var(--border2)", borderRadius:10,
          overflow:"hidden", boxShadow:"0 16px 48px rgba(0,0,0,0.4)",
        }}>
          {suggestions.map((s, i) => (
            <div key={s.place_id || i}
              onMouseDown={() => pick(s)}
              style={{
                padding:"11px 14px", fontSize:13, cursor:"pointer",
                borderBottom: i < suggestions.length-1 ? "1px solid var(--border)" : "none",
                display:"flex", alignItems:"flex-start", gap:10,
                transition:"background 0.1s",
              }}
              onMouseOver={e => e.currentTarget.style.background="var(--bg4)"}
              onMouseOut={e  => e.currentTarget.style.background="transparent"}
            >
              <MapPin size={13} style={{ color:"var(--accent)", marginTop:2, flexShrink:0 }}/>
              <div>
                <div style={{ fontWeight:600, color:"var(--text)" }}>{s.main_text}</div>
                <div style={{ fontSize:11, color:"var(--text3)", marginTop:1 }}>{s.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Order Form ────────────────────────────────────────────────────────────────
function OrderForm() {
  const { user, guestLogin } = useAuth();
  const navigate = useNavigate();

  const [svc,     setSvc]    = useState("parcel");
  const [name,    setName]   = useState(user?.name  || "");
  const [phone,   setPhone]  = useState(user?.phone || "");
  const [pickup,  setPickup] = useState("");
  const [dropoff, setDropoff]= useState("");
  const [payment, setPayment]= useState("cash");
  const [notes,   setNotes]  = useState("");
  const [loading, setLoading]= useState(false);
  const [errors,  setErrors] = useState({});
  const [pwCopied,  setPwCopied]  = useState(false);
  const [pwVisible, setPwVisible] = useState(false);
  const [success,   setSuccess]   = useState(null);
  const [liveStatus,setLiveStatus]= useState("pending");

  const [resolvedCoords, setResolvedCoords] = useState(null);
  const [resolving, setResolving] = useState(false);
  const resolveTimer = useRef();
  const pollRef      = useRef();

  const [paymentState, setPaymentState] = useState("idle");
  const [pendingOrder, setPendingOrder] = useState(null);
  const stopPolling   = useRef();

  useEffect(() => {
    if (user) { setName(user.name||""); setPhone(user.phone||""); }
  }, [user]);

  const resolve = useCallback(async (pu, dr) => {
    if (pu.length < 5 || dr.length < 5) { setResolvedCoords(null); return; }
    setResolving(true);
    try {
      const result = await resolveDelivery(pu, dr);
      setResolvedCoords(result);
    } catch(e) {
      setResolvedCoords(null);
    }
    setResolving(false);
  }, []);

  useEffect(() => {
    clearTimeout(resolveTimer.current);
    setResolvedCoords(null);
    if (pickup.length >= 5 && dropoff.length >= 5) {
      resolveTimer.current = setTimeout(() => resolve(pickup, dropoff), 900);
    }
    return () => clearTimeout(resolveTimer.current);
  }, [pickup, dropoff, resolve]);

  useEffect(() => {
    if (!success) return;
    const token = localStorage.getItem("kq_token");
    pollRef.current = setInterval(async () => {
      try {
        const r = await ordersAPI.track(success.order_id);
        const st = r.data.data?.status;
        if (st) setLiveStatus(st);
        if (st === "delivered") clearInterval(pollRef.current);
      } catch {}
    }, 6000);
    return () => clearInterval(pollRef.current);
  }, [success]);

  const validate = () => {
    const e = {};
    if (!name.trim())    e.name    = true;
    if (!phone.trim())   e.phone   = true;
    if (!pickup.trim())  e.pickup  = true;
    if (!dropoff.trim()) e.dropoff = true;
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async () => {
    if (!validate()) { toast.error("Please fill in all required fields"); return; }

    if (pendingOrder) {
      retryPayment();
      return;
    }

    let coords = resolvedCoords;
    if (!coords) {
      setResolving(true);
      try {
        coords = await resolveDelivery(pickup, dropoff);
        setResolvedCoords(coords);
      } catch(e) {
        setResolving(false);
        toast.error(`Could not find those addresses: ${e.message}`);
        return;
      }
      setResolving(false);
    }

    setLoading(true);
    try {
      let isNew = false, generatedPassword = null, token = localStorage.getItem("kq_token");
      if (!user) {
        const authResult = await guestLogin(name, phone);
        isNew = authResult.isNew;
        generatedPassword = authResult.generatedPassword;
        token = localStorage.getItem("kq_token");
        if (!isNew) toast(`👋 Welcome back, ${name.split(" ")[0]}!`, { icon: "⚡" });
      }

      const r = await ordersAPI.create({
        pickup_address:  coords.pickup.formatted  || pickup,
        dropoff_address: coords.dropoff.formatted || dropoff,
        pickup_lat:      coords.pickup.lat,
        pickup_lng:      coords.pickup.lng,
        dropoff_lat:     coords.dropoff.lat,
        dropoff_lng:     coords.dropoff.lng,
        service_type:    svc,
        payment_method:  payment,
        notes:           notes || undefined,
        distance_km:     coords.distanceKm,
      });

      const orderData = r.data.data;
      const oid = orderData.order_id || orderData.id;

      if (payment === "cash" || !orderData.requires_payment) {
        setSuccess({ order_id: oid, isNew, generatedPassword, customerName: name, customerPhone: phone });
        setLiveStatus("pending");
        toast.success(`✅ Order #${oid} placed! Finding your rider...`);
        setLoading(false);
        return;
      }

      const payRes = await fetch(`${process.env.REACT_APP_API_URL}/payments/initialize`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ order_id: oid }),
      });
      const payData = await payRes.json();

      if (!payRes.ok) throw new Error(payData.message || "Failed to initialize payment");

      if (payData.data.payment_method === "wallet") {
        setSuccess({ order_id: oid, isNew, generatedPassword, customerName: name, customerPhone: phone });
        setLiveStatus("pending");
        toast.success(`✅ GHS ${payData.data.amount.toFixed(2)} deducted from wallet. Finding your rider!`);
        setLoading(false);
        return;
      }

      const { access_code, reference, amount } = payData.data;

      setPendingOrder({ order_id: oid, price: amount, access_code, reference, payment_method: payment, isNew, generatedPassword, customerName: name, customerPhone: phone });
      setPaymentState("awaiting_payment");
      setLoading(false);

      const userEmail = user?.email || `${phone.replace(/\D/g, "")}@kwikie.com`;

      openPaystackPopup({
        email:          userEmail,
        amount,
        reference,
        access_code,
        payment_method: payment,
        onSuccess: async (ref) => {
          setPaymentState("verifying");
          toast.loading("Confirming payment...", { id: "pay-confirm" });
          try {
            const vRes = await fetch(`${process.env.REACT_APP_API_URL}/payments/verify/${ref}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const vData = await vRes.json();
            if (vRes.ok) {
              toast.dismiss("pay-confirm");
              toast.success("✅ Payment confirmed! Finding your rider...");
              setPaymentState("paid");
              setSuccess({ order_id: oid, isNew, generatedPassword, customerName: name, customerPhone: phone });
              setLiveStatus("pending");
              return;
            }
          } catch {}
          stopPolling.current = pollPaymentStatus(
            oid, token,
            (newStatus) => {
              toast.dismiss("pay-confirm");
              toast.success("✅ Payment confirmed! Finding your rider...");
              setPaymentState("paid");
              setSuccess({ order_id: oid, isNew, generatedPassword, customerName: name, customerPhone: phone });
              setLiveStatus(newStatus || "pending");
            },
            (reason) => {
              toast.dismiss("pay-confirm");
              if (reason === "timeout") {
                toast("Payment is being verified. Check your orders for updates.", { icon: "⏳" });
              } else {
                toast.error("Payment could not be confirmed. Please try again.");
              }
              setPaymentState("failed");
            }
          );
        },
        onClose: () => {
          setPaymentState("idle");
          toast("Payment cancelled. Your order is saved — pay when ready.", { icon: "ℹ️" });
        },
      });

    } catch(e) {
      toast.error(e.response?.data?.message || e.message || "Something went wrong");
      setLoading(false);
    }
  };

  const retryPayment = async () => {
    if (!pendingOrder) return;
    const token = localStorage.getItem("kq_token");
    const userEmail = user?.email || `${phone.replace(/\D/g,"")}@kwikie.com`;
    setPaymentState("awaiting_payment");
    openPaystackPopup({
      email:          userEmail,
      amount:         pendingOrder.price,
      reference:      pendingOrder.reference,
      access_code:    pendingOrder.access_code,
      payment_method: pendingOrder.payment_method,
      onSuccess: async (ref) => {
        setPaymentState("verifying");
        toast.loading("Confirming payment...", { id: "pay-confirm" });
        stopPolling.current = pollPaymentStatus(
          pendingOrder.order_id, token,
          () => {
            toast.dismiss("pay-confirm");
            toast.success("✅ Payment confirmed!");
            setPaymentState("paid");
            setSuccess({ order_id: pendingOrder.order_id, isNew: pendingOrder.isNew, generatedPassword: pendingOrder.generatedPassword, customerName: pendingOrder.customerName, customerPhone: pendingOrder.customerPhone });
            setLiveStatus("pending");
          },
          () => {
            toast.dismiss("pay-confirm");
            toast.error("Could not confirm payment. Try again.");
            setPaymentState("idle");
          }
        );
      },
      onClose: () => setPaymentState("idle"),
    });
  };

  const reset = () => {
    clearInterval(pollRef.current);
    if (stopPolling.current) stopPolling.current();
    setSuccess(null); setLiveStatus("pending");
    setPickup(""); setDropoff(""); setNotes("");
    setResolvedCoords(null); setErrors({});
    setPwCopied(false); setPwVisible(false);
    setPaymentState("idle"); setPendingOrder(null);
    if (!user) { setName(""); setPhone(""); }
  };

  // ── Success view ────────────────────────────────────────────────────────────
  if (success) {
    const currentIdx = ORDER_STATUSES.findIndex(s => s.key === liveStatus);
    return (
      <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:20, padding:"32px 24px", boxShadow:"0 40px 80px rgba(0,0,0,0.3)" }}>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ fontSize:52, marginBottom:12, animation:"bounce 0.6s ease both" }}>🎉</div>
          <div style={{ fontFamily:"var(--font-head)", fontSize:22, fontWeight:800, marginBottom:6 }}>Order Placed!</div>
          <p style={{ fontSize:14, color:"var(--text2)", lineHeight:1.6, marginBottom:20 }}>
            A KwiKie rider is being dispatched to your pickup location.
          </p>
          <div style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:8, display:"inline-block", padding:"10px 24px", fontFamily:"var(--font-mono)", fontSize:16, color:"var(--accent)", letterSpacing:1 }}>
            Order #{success.order_id}
          </div>
        </div>

        {success.isNew && success.generatedPassword && (
          <div style={{ background:"rgba(245,166,35,0.07)", border:"1px solid rgba(245,166,35,0.3)", borderRadius:14, padding:20, marginBottom:20, animation:"fadeUp 0.5s 0.3s ease both", animationFillMode:"forwards", opacity:0 }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <span style={{ fontSize:22, flexShrink:0 }}>🔐</span>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"var(--font-head)", fontSize:15, fontWeight:700, marginBottom:4 }}>Your account was created</div>
                <p style={{ fontSize:13, color:"var(--text2)", marginBottom:14, lineHeight:1.55 }}>
                  Save your password to track this and future orders at <strong style={{ color:"var(--accent)" }}>kwikkie.app/login</strong>
                </p>
                <div style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, marginBottom:10 }}>
                  <div>
                    <div style={{ fontSize:11, color:"var(--text3)", fontFamily:"var(--font-mono)", marginBottom:3 }}>YOUR PASSWORD</div>
                    <div style={{ fontFamily:"var(--font-mono)", fontSize:15, color:"var(--text)", letterSpacing: pwVisible ? 0.5 : 2 }}>
                      {pwVisible ? success.generatedPassword : "•".repeat(success.generatedPassword.length)}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                    <button className="btn-ghost btn-sm" onClick={() => setPwVisible(v=>!v)}>{pwVisible?"Hide":"Show"}</button>
                    <button className="btn-primary btn-sm" onClick={() => { navigator.clipboard?.writeText(success.generatedPassword); setPwCopied(true); setTimeout(()=>setPwCopied(false),2500); }}>
                      {pwCopied ? "✓ Copied!" : "Copy"}
                    </button>
                  </div>
                </div>
                <p style={{ fontSize:12, color:"var(--text3)" }}>
                  📱 Phone: <span style={{ color:"var(--text2)", fontFamily:"var(--font-mono)" }}>{success.customerPhone}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        <div style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:12, padding:20, marginBottom:20 }}>
          <div style={{ fontFamily:"var(--font-head)", fontSize:14, fontWeight:700, marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:8, height:8, borderRadius:"50%", background:"var(--accent)", display:"inline-block", animation:"pulse 1.5s infinite" }}/>
            Live Tracking
          </div>
          {ORDER_STATUSES.map((s, i) => {
            const done   = i < currentIdx;
            const active = i === currentIdx;
            return (
              <div key={s.key} style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"7px 0" }}>
                <div style={{ width:10, height:10, borderRadius:"50%", marginTop:5, flexShrink:0, background: done?"var(--green)":active?"var(--accent)":"var(--bg4)", border: done||active?"none":"1px solid var(--border2)", boxShadow: active?"0 0 8px rgba(245,166,35,0.6)":"none" }}/>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color: i<=currentIdx?"var(--text)":"var(--text3)" }}>{s.label}</div>
                  {active && <div style={{ fontSize:11, color:"var(--text3)", marginTop:2 }}>In progress...</div>}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display:"flex", gap:10 }}>
          <button className="btn-ghost"   style={{ flex:1, justifyContent:"center" }} onClick={reset}>New delivery</button>
          <button className="btn-primary" style={{ flex:1, justifyContent:"center" }} onClick={() => navigate("/orders")}>View all orders</button>
        </div>
      </div>
    );
  }

  // ── Order form ──────────────────────────────────────────────────────────────
  return (
    <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:20, padding:"32px 24px", boxShadow:"0 40px 80px rgba(0,0,0,0.3)" }}>
      <div style={{ fontFamily:"var(--font-head)", fontSize:22, fontWeight:800, marginBottom:5 }}>⚡ Request a Delivery</div>
      <p style={{ fontSize:13, color:"var(--text2)", marginBottom:24 }}>Fill in the details — takes 30 seconds</p>

      <div className="form-group">
        <div className="form-label">Service Type</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:2 }}>
          {SERVICES.map(s => (
            <div key={s.id} onClick={() => setSvc(s.id)} style={{
              display:"flex", flexDirection:"column", alignItems:"center", gap:5,
              padding:"12px 6px", borderRadius:10, cursor:"pointer",
              border:`1.5px solid ${svc===s.id?"var(--accent)":"var(--border)"}`,
              background: svc===s.id ? "rgba(245,166,35,0.08)" : "var(--bg3)",
              color: svc===s.id ? "var(--accent)" : "var(--text2)",
              fontSize:11, fontWeight:600, transition:"all 0.15s", userSelect:"none",
            }}>
              <span style={{ fontSize:22 }}>{s.icon}</span>{s.label}
            </div>
          ))}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Your Name *</label>
          <input className={`form-input ${errors.name?"err":""}`} placeholder="Kwame Mensah"
            value={name} onChange={e=>{setName(e.target.value);setErrors(p=>({...p,name:false}));}}/>
        </div>
        <div className="form-group">
          <label className="form-label">Phone Number *</label>
          <input className={`form-input ${errors.phone?"err":""}`} placeholder="+233 XX XXX XXXX"
            value={phone} onChange={e=>{setPhone(e.target.value);setErrors(p=>({...p,phone:false}));}}/>
        </div>
      </div>

      <AddressInput
        label="📍 Pickup Address *"
        placeholder="e.g. Accra Mall, Spintex Road"
        value={pickup}
        error={errors.pickup}
        onChange={v => { setPickup(v); setErrors(p=>({...p,pickup:false})); }}
        onSelect={v => setPickup(v)}
      />
      <AddressInput
        label="🏁 Dropoff Address *"
        placeholder="e.g. East Legon, Accra"
        value={dropoff}
        error={errors.dropoff}
        onChange={v => { setDropoff(v); setErrors(p=>({...p,dropoff:false})); }}
        onSelect={v => setDropoff(v)}
      />

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Payment</label>
          <select className="form-select" value={payment} onChange={e=>setPayment(e.target.value)}>
            <option value="cash">💵 Cash on delivery</option>
            <option value="mobile_money">📱 Mobile Money</option>
            <option value="card">💳 Card</option>
            <option value="wallet">👛 Wallet Balance</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Notes (optional)</label>
          <input className="form-input" placeholder="Fragile, call on arrival..." value={notes} onChange={e=>setNotes(e.target.value)}/>
        </div>
      </div>

      {resolving && (
        <div style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:10, marginBottom:14, fontSize:13, color:"var(--text3)" }}>
          <Loader size={14} style={{ animation:"spin 0.8s linear infinite", flexShrink:0 }}/>
          Calculating route and price...
        </div>
      )}
      {resolvedCoords && !resolving && (
        <div style={{ background:"rgba(245,166,35,0.08)", border:"1px solid rgba(245,166,35,0.25)", borderRadius:10, padding:"14px 18px", marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
            <div>
              <div style={{ fontSize:12, color:"var(--text2)", fontWeight:600 }}>Estimated delivery fee</div>
              <div style={{ fontSize:11, color:"var(--text3)", marginTop:2 }}>
                📍 {resolvedCoords.distanceText} · 🕐 ~{resolvedCoords.durationMins} mins
              </div>
            </div>
            <div style={{ fontFamily:"var(--font-head)", fontSize:28, fontWeight:800, color:"var(--accent)" }}>
              GHS {resolvedCoords.price}
            </div>
          </div>
          <div style={{ borderTop:"1px solid rgba(245,166,35,0.15)", paddingTop:10, display:"flex", flexDirection:"column", gap:4 }}>
            <div style={{ fontSize:11, color:"var(--text3)" }}>
              📍 <span style={{ color:"var(--text2)" }}>{resolvedCoords.pickup.formatted}</span>
            </div>
            <div style={{ fontSize:11, color:"var(--text3)" }}>
              🏁 <span style={{ color:"var(--text2)" }}>{resolvedCoords.dropoff.formatted}</span>
            </div>
          </div>
        </div>
      )}

      {(paymentState === "awaiting_payment" || paymentState === "verifying") && pendingOrder && (
        <div style={{ background:"rgba(245,166,35,0.08)", border:"1px solid rgba(245,166,35,0.35)", borderRadius:12, padding:20, marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
            {paymentState === "verifying"
              ? <Loader size={18} style={{ color:"var(--accent)", animation:"spin 0.8s linear infinite" }}/>
              : <span style={{ fontSize:22 }}>💳</span>
            }
            <div>
              <div style={{ fontWeight:700, fontSize:14 }}>
                {paymentState === "verifying" ? "Confirming your payment..." : "Complete your payment"}
              </div>
              <div style={{ fontSize:12, color:"var(--text3)", marginTop:2 }}>
                Order #{pendingOrder.order_id} · GHS {pendingOrder.price}
              </div>
            </div>
          </div>
          {paymentState === "awaiting_payment" && (
            <div style={{ display:"flex", gap:8 }}>
              <button className="btn-primary" style={{ flex:1, justifyContent:"center", padding:"10px 0" }} onClick={retryPayment}>
                Open Payment Window
              </button>
              <button className="btn-ghost" style={{ padding:"10px 16px" }} onClick={() => { setPaymentState("idle"); setPendingOrder(null); }}>
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      <button onClick={submit} disabled={loading || resolving || paymentState === "verifying"} style={{
        width:"100%", padding:16, borderRadius:12, background:"var(--accent)", color:"#000",
        border:"none", fontFamily:"var(--font-head)", fontWeight:800, fontSize:16,
        cursor: loading||resolving||paymentState==="verifying" ? "not-allowed" : "pointer",
        display:"flex", alignItems:"center", justifyContent:"center", gap:8,
        transition:"transform 0.15s, box-shadow 0.2s", marginTop:4,
        opacity: loading||resolving||paymentState==="verifying" ? 0.7 : 1,
      }}
        onMouseOver={e=>!loading&&!resolving&&paymentState!=="verifying"&&(e.currentTarget.style.transform="translateY(-2px)")}
        onMouseOut={e=>e.currentTarget.style.transform="none"}
      >
        {loading ? <><span className="spinner"/> Creating order...</>
         : resolving ? <><Loader size={16} style={{ animation:"spin 0.8s linear infinite" }}/> Calculating route...</>
         : payment !== "cash" ? "⚡ Continue to Payment"
         : "⚡ Request Delivery Now"}
      </button>
      <p style={{ fontSize:11, color:"var(--text3)", textAlign:"center", marginTop:10 }}>
        By submitting you agree to our <a href="#" style={{ color:"var(--accent)" }}>Terms of Service</a>
      </p>
    </div>
  );
}


// ── Home Page ─────────────────────────────────────────────────────────────────
export default function Home() {
  useReveal();
  const isMobile = useWindowWidth() < 768;
  const isTablet = useWindowWidth() < 1024;

  return (
    <div>
      {/* ── Hero ── */}
      <section style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", padding: isMobile ? "100px 5% 60px" : "120px 6% 80px", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:"linear-gradient(rgba(255,255,255,0.022) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.022) 1px,transparent 1px)", backgroundSize:"60px 60px", maskImage:"radial-gradient(ellipse 80% 60% at 50% 0%,black 20%,transparent 100%)", pointerEvents:"none" }}/>
        <div style={{ position:"absolute", top:"8%", left:"50%", transform:"translateX(-50%)", width:800, height:500, background:"radial-gradient(ellipse,rgba(245,166,35,0.09) 0%,transparent 70%)", pointerEvents:"none" }}/>

        <div className="animate-fadeUp" style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(245,166,35,0.1)", border:"1px solid rgba(245,166,35,0.25)", color:"var(--accent)", borderRadius:100, padding:"6px 18px", fontSize:13, fontWeight:600, marginBottom:28 }}>
          ⚡ Open Delivery Infrastructure · Ghana 🇬🇭
        </div>
        <h1 className="animate-fadeUp" style={{ fontSize:"clamp(38px,8vw,96px)", fontWeight:800, letterSpacing:"-3px", lineHeight:0.96, marginBottom:24, animationDelay:"0.1s" }}>
          Deliver <span style={{ background:"linear-gradient(135deg,var(--accent),var(--accent2))", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>anything,</span><br/>anywhere in the city
        </h1>
        <p className="animate-fadeUp" style={{ fontSize:"clamp(15px,2vw,20px)", color:"var(--text2)", maxWidth:560, margin:"0 auto 40px", animationDelay:"0.2s" }}>
          Plug KwiKie's rider network into your business with a single API call. Free to start, real-time tracking, OTP-confirmed delivery.
        </p>
        <div className="animate-fadeUp" style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap", animationDelay:"0.3s" }}>
          <button className="btn-primary" onClick={() => document.getElementById("order")?.scrollIntoView({ behavior:"smooth" })}>⚡ Request a Delivery</button>
          <button className="btn-ghost"   onClick={() => document.getElementById("api")?.scrollIntoView({ behavior:"smooth" })}>View API Docs →</button>
        </div>

        {/* Stats */}
        <div className="animate-fadeUp" style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: isMobile ? "24px 16px" : 48, justifyContent:"center", marginTop:72, paddingTop:48, borderTop:"1px solid var(--border)", width:"100%", maxWidth:680, animationDelay:"0.45s" }}>
          {[["5 min","Avg pickup time"],["300+","Active riders"],["Free","API access"],["24/7","Operations"]].map(([n,l]) => (
            <div key={l} style={{ textAlign:"center" }}>
              <div style={{ fontFamily:"var(--font-head)", fontSize: isMobile ? 30 : 38, fontWeight:800, color:"var(--accent)" }}>{n}</div>
              <div style={{ fontSize:11, color:"var(--text3)", fontWeight:600, letterSpacing:"0.8px", textTransform:"uppercase", marginTop:3 }}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="how" className="section section-alt">
        <div className="container">
          <div className="section-tag">Process</div>
          <h2 className="section-title">Simple from start<br/>to doorstep</h2>
          <p className="section-sub">Whether you're a restaurant, pharmacy, or e-commerce shop — a delivery takes 3 steps.</p>
          <div className="reveal" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:24, marginTop:56 }}>
            {[
              { n:"01", icon:"📦", title:"Place the order",    desc:"Use our website, app, or one API call with pickup and dropoff. That's it." },
              { n:"02", icon:"🏍️", title:"Rider dispatched",   desc:"Our system finds the nearest available rider and dispatches them within seconds." },
              { n:"03", icon:"📍", title:"Track in real-time", desc:"Live GPS on every order. Your customer sees exactly where their package is." },
              { n:"04", icon:"✅", title:"Confirmed delivery",  desc:"OTP verification and photo proof on every drop-off. Full accountability." },
            ].map(s => (
              <div key={s.n} style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:"var(--radius)", padding:"32px 28px", position:"relative", overflow:"hidden", transition:"border-color 0.2s, transform 0.2s", cursor:"default" }}
                onMouseOver={e=>{e.currentTarget.style.borderColor="rgba(245,166,35,0.3)";e.currentTarget.style.transform="translateY(-3px)";}}
                onMouseOut={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.transform="none";}}>
                <div style={{ fontFamily:"var(--font-head)", fontSize:72, fontWeight:800, color:"rgba(245,166,35,0.06)", position:"absolute", top:8, right:18, lineHeight:1, pointerEvents:"none" }}>{s.n}</div>
                <div style={{ fontSize:30, marginBottom:16 }}>{s.icon}</div>
                <div style={{ fontFamily:"var(--font-head)", fontSize:18, fontWeight:700, marginBottom:8 }}>{s.title}</div>
                <div style={{ fontSize:14, color:"var(--text2)", lineHeight:1.65 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── API ── */}
      <section id="api" className="section" style={{ background:"var(--bg)" }}>
        <div className="container">
          <div className="section-tag">For Developers</div>
          <h2 className="section-title">One API.<br/>Infinite deliveries.</h2>
          <p className="section-sub">The KwiKie API is free, open, and built for speed. Integrate delivery into your product in under an hour.</p>
          <div className="reveal" style={{ display:"grid", gridTemplateColumns: isTablet ? "1fr" : "1fr 1fr", gap: isTablet ? 40 : 64, alignItems:"start", marginTop:56 }}>
            <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
              {[
                { icon:"🔑", title:"Free API Key",        desc:"Register your business, get a key instantly. No credit card, no contract." },
                { icon:"🔁", title:"Webhook Callbacks",   desc:"Get notified at every stage — picked up, on the way, delivered. Push to your backend URL." },
                { icon:"📡", title:"Real-Time Tracking",  desc:"Embed live rider GPS into your own app. Full tracking data via REST or WebSocket." },
                { icon:"⚡", title:"Simple REST",         desc:"Standard JSON API. Create an order in one POST. Works with Python, JS, PHP, anything." },
              ].map(f => (
                <div key={f.title} style={{ display:"flex", gap:16, alignItems:"flex-start", padding:20, borderRadius:12, border:"1px solid var(--border)", background:"var(--bg2)", transition:"border-color 0.2s" }}
                  onMouseOver={e=>e.currentTarget.style.borderColor="rgba(245,166,35,0.25)"} onMouseOut={e=>e.currentTarget.style.borderColor="var(--border)"}>
                  <div style={{ width:40, height:40, borderRadius:10, flexShrink:0, background:"rgba(245,166,35,0.1)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>{f.icon}</div>
                  <div>
                    <div style={{ fontFamily:"var(--font-head)", fontSize:15, fontWeight:700, marginBottom:4 }}>{f.title}</div>
                    <p style={{ fontSize:13, color:"var(--text2)", lineHeight:1.55 }}>{f.desc}</p>
                  </div>
                </div>
              ))}
              <ApiKeyForm/>
            </div>
            {/* Code window — hidden on mobile to avoid horizontal scroll */}
            {!isMobile && (
              <div>
                <CodeWindow/>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="section section-alt">
        <div className="container">
          <div className="section-tag">Pricing</div>
          <h2 className="section-title">Pay per delivery.<br/>Nothing else.</h2>
          <p className="section-sub">No monthly fees. No setup costs. You only pay when a delivery is made.</p>
          <div className="reveal" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:24, marginTop:56 }}>
            {[
              { name:"Starter", price:"Free", unit:"/ always", desc:"Perfect for small businesses getting started.", featured:false,
                features:["Free API access","Up to 50 deliveries/month","Real-time tracking","Basic webhook support","Email support"],
                btn:"Get Started Free" },
              { name:"Business", price:"GHS 20", unit:"/ delivery", desc:"For growing businesses with regular delivery needs.", featured:true,
                features:["Unlimited deliveries","Priority dispatch","Full webhook support","Dedicated account manager","Custom branding","Analytics dashboard"],
                btn:"Start Delivering →" },
              { name:"Enterprise", price:"Custom", unit:"", desc:"For large operations and high-volume integrations.", featured:false,
                features:["Volume pricing","SLA guarantees","Dedicated rider fleet","White-label options","24/7 priority support","Custom integrations"],
                btn:"Contact Sales" },
            ].map(p => (
              <div key={p.name} style={{ background:"var(--bg3)", border:`1px solid ${p.featured?"var(--accent)":"var(--border)"}`, borderRadius:"var(--radius)", padding:"36px 32px", position:"relative", transition:"transform 0.2s", boxShadow: p.featured ? "0 0 40px rgba(245,166,35,0.1)" : "none" }}
                onMouseOver={e=>e.currentTarget.style.transform="translateY(-4px)"} onMouseOut={e=>e.currentTarget.style.transform="none"}>
                {p.featured && <div style={{ position:"absolute", top:-13, left:"50%", transform:"translateX(-50%)", background:"var(--accent)", color:"#000", fontFamily:"var(--font-head)", fontSize:11, fontWeight:800, padding:"4px 16px", borderRadius:100, whiteSpace:"nowrap" }}>MOST POPULAR</div>}
                <div style={{ fontFamily:"var(--font-head)", fontSize:15, fontWeight:700, color:"var(--text2)", marginBottom:14 }}>{p.name}</div>
                <div style={{ fontFamily:"var(--font-head)", fontSize:33, fontWeight:800, letterSpacing:"-2px", lineHeight:1, marginBottom:8 }}>
                  {p.price} <span style={{ fontSize:18, fontWeight:400, color:"var(--text3)" }}>{p.unit}</span>
                </div>
                <div style={{ fontSize:13, color:"var(--text2)", marginBottom:24 }}>{p.desc}</div>
                <ul style={{ listStyle:"none", display:"flex", flexDirection:"column", gap:10, marginBottom:28 }}>
                  {p.features.map(f => (
                    <li key={f} style={{ fontSize:14, color:"var(--text2)", display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ color:"var(--green)", fontWeight:700 }}>✓</span> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => p.btn==="Contact Sales" ? window.location="mailto:business@kwikkie.app" : document.getElementById("order")?.scrollIntoView({ behavior:"smooth" })}
                  style={{ width:"100%", padding:13, borderRadius:10, fontFamily:"var(--font-head)", fontWeight:700, fontSize:14, cursor:"pointer", transition:"all 0.15s", background: p.featured?"var(--accent)":"transparent", color: p.featured?"#000":"var(--text)", border: p.featured?"none":"1px solid var(--border2)" }}
                  onMouseOver={e=>{ if(!p.featured){e.currentTarget.style.borderColor="var(--accent)";e.currentTarget.style.color="var(--accent)";} else{e.currentTarget.style.boxShadow="0 4px 20px rgba(245,166,35,0.35)";} }}
                  onMouseOut={e=>{ if(!p.featured){e.currentTarget.style.borderColor="var(--border2)";e.currentTarget.style.color="var(--text)";} else{e.currentTarget.style.boxShadow="none";} }}>
                  {p.btn}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Quick Delivery ── */}
      <section id="order" className="section" style={{ background:"var(--bg)", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-200, left:"50%", transform:"translateX(-50%)", width:900, height:700, background:"radial-gradient(ellipse,rgba(245,166,35,0.055) 0%,transparent 70%)", pointerEvents:"none" }}/>
        <div className="container">
          <div style={{ display:"grid", gridTemplateColumns: isTablet ? "1fr" : "1fr 1fr", gap: isTablet ? 40 : 80, alignItems:"center" }}>
            {/* Left copy — shown above form on mobile */}
            <div className="reveal">
              <div className="section-tag">Quick Delivery</div>
              <h2 style={{ fontSize:"clamp(26px,3.5vw,46px)", fontWeight:800, letterSpacing:"-1.5px", marginBottom:20 }}>
                Need something<br/>delivered <span style={{ background:"linear-gradient(135deg,var(--accent),var(--accent2))", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>right now?</span>
              </h2>
              <p style={{ fontSize:16, color:"var(--text2)", lineHeight:1.7, marginBottom:32 }}>
                Fill the form and a KwiKie rider will be at your pickup location in minutes. No app needed — just fill and go.
              </p>
              {/* Hide feature list on mobile to keep it tight */}
              {!isMobile && (
                <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                  {[
                    { icon:<Zap size={18}/>,         title:"Instant dispatch",       desc:"Nearest rider assigned automatically within seconds" },
                    { icon:<MapPin size={18}/>,       title:"Live GPS tracking",      desc:"Track your rider on a map in real-time after booking" },
                    { icon:<CheckCircle size={18}/>,  title:"Delivery confirmation",  desc:"OTP + photo proof on every completed delivery" },
                    { icon:<Shield size={18}/>,       title:"Pay on delivery",        desc:"Cash, mobile money or card — your choice" },
                  ].map(t => (
                    <div key={t.title} style={{ display:"flex", alignItems:"center", gap:14 }}>
                      <div style={{ width:42, height:42, borderRadius:10, flexShrink:0, background:"var(--bg3)", border:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"center", color:"var(--accent)" }}>{t.icon}</div>
                      <div>
                        <div style={{ fontFamily:"var(--font-head)", fontSize:14, fontWeight:700, marginBottom:2 }}>{t.title}</div>
                        <div style={{ fontSize:13, color:"var(--text2)" }}>{t.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Right form */}
            <div className="reveal"><OrderForm/></div>
          </div>
        </div>
      </section>
    </div>
  );
}